import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { BellIcon, CheckCheckIcon, FilterIcon } from 'lucide-react'
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { toast } from 'sonner'

import { ResponsivePageHeader } from '@nebula/shared/components'
import {
  notificationsApi,
  getNotificationIcon,
  getNotificationTypeColor,
  getNotificationTypeBgColor,
  type Notification,
} from '@nebula/shared/services/notificationsApi'
import { useAuthStore } from '@nebula/shared/store'
import { formatRelativeTime, formatDate } from '@nebula/shared/utils/format'

const TYPE_OPTIONS = [
  { value: '', label: 'Todos os tipos' },
  { value: 'vm_provisioned', label: 'VM Provisionada' },
  { value: 'vm_provision_failed', label: 'Erro VM' },
  { value: 'vm_deleted', label: 'VM Removida' },
  { value: 'backup_completed', label: 'Backup Concluído' },
  { value: 'backup_failed', label: 'Backup Falhou' },
  { value: 'restore_completed', label: 'Restauração Concluída' },
  { value: 'restore_failed', label: 'Restauração Falhou' },
  { value: 'support_reply', label: 'Resposta Suporte' },
  { value: 'support_status_changed', label: 'Status Suporte' },
  { value: 'vpc_created', label: 'VPC Criada' },
  { value: 'vpc_deleted', label: 'VPC Removida' },
  { value: 'vpc_updated', label: 'VPC Atualizada' },
  { value: 'system', label: 'Sistema' },
]

const READ_OPTIONS = [
  { value: '', label: 'Todas' },
  { value: 'false', label: 'Não lidas' },
  { value: 'true', label: 'Lidas' },
]

const NotificationsPage = () => {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const activeOrgId = useAuthStore((s) => s.user?.organization_id)

  const [typeFilter, setTypeFilter] = useState('')
  const [readFilter, setReadFilter] = useState('')
  const [page, setPage] = useState(0)
  const limit = 20

  const { data, isLoading } = useQuery({
    queryKey: ['notifications', activeOrgId, typeFilter, readFilter, page],
    queryFn: () =>
      notificationsApi.list({
        type: typeFilter || undefined,
        is_read: readFilter ? readFilter === 'true' : undefined,
        portal: 'ec',
        skip: page * limit,
        limit,
      }),
  })

  const markAllMutation = useMutation({
    mutationFn: () => notificationsApi.markAsRead(null),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] })
      queryClient.invalidateQueries({ queryKey: ['notifications-unread-count'] })
      toast.success('Todas as notificações foram marcadas como lidas')
    },
  })

  const dismissMutation = useMutation({
    mutationFn: (id: string) => notificationsApi.dismiss(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] })
      queryClient.invalidateQueries({ queryKey: ['notifications-unread-count'] })
    },
  })

  const handleClick = (notification: Notification) => {
    if (!notification.is_read) {
      notificationsApi.markOneAsRead(notification.id).then(() => {
        queryClient.invalidateQueries({ queryKey: ['notifications'] })
        queryClient.invalidateQueries({ queryKey: ['notifications-unread-count'] })
      })
    }
    if (notification.action_url) {
      navigate(notification.action_url)
    }
  }

  const notifications = data?.notifications ?? []
  const total = data?.total ?? 0
  const unreadCount = data?.unread_count ?? 0
  const totalPages = Math.ceil(total / limit)

  return (
    <div className="mx-auto max-w-4xl space-y-6 p-4 sm:p-6">
      <ResponsivePageHeader
        title="Notificações"
        description={
          unreadCount > 0 ? `${unreadCount} não lida${unreadCount === 1 ? '' : 's'}` : 'Tudo em dia'
        }
      />

      {/* Filters + Actions */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-2">
          <FilterIcon className="size-4 text-gray-400" />
          <select
            value={typeFilter}
            onChange={(e) => {
              setTypeFilter(e.target.value)
              setPage(0)
            }}
            className="rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-sm text-gray-700 focus:border-blue-500 focus:outline-none"
          >
            {TYPE_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
          <select
            value={readFilter}
            onChange={(e) => {
              setReadFilter(e.target.value)
              setPage(0)
            }}
            className="rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-sm text-gray-700 focus:border-blue-500 focus:outline-none"
          >
            {READ_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>

        {unreadCount > 0 && (
          <button
            type="button"
            onClick={() => markAllMutation.mutate()}
            disabled={markAllMutation.isPending}
            className="ml-auto flex items-center gap-1.5 rounded-lg bg-blue-50 px-3 py-1.5 text-sm font-medium text-blue-700 hover:bg-blue-100 disabled:opacity-50"
          >
            <CheckCheckIcon className="size-4" />
            Marcar todas como lidas
          </button>
        )}
      </div>

      {/* List */}
      <div className="overflow-hidden rounded-xl border border-gray-200 bg-white">
        {isLoading ? (
          <div className="flex items-center justify-center py-16">
            <div className="size-6 animate-spin rounded-full border-2 border-gray-300 border-t-blue-500" />
          </div>
        ) : notifications.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-gray-400">
            <BellIcon className="mb-3 size-10" />
            <p className="text-sm font-medium">Nenhuma notificação encontrada</p>
            <p className="mt-1 text-xs">
              {typeFilter || readFilter
                ? 'Tente ajustar os filtros'
                : 'Você será notificado sobre eventos importantes'}
            </p>
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {notifications.map((n) => {
              const Icon = getNotificationIcon(n.type)
              const iconColor = getNotificationTypeColor(n.type)
              const iconBg = getNotificationTypeBgColor(n.type)

              return (
                <div
                  key={n.id}
                  role="button"
                  tabIndex={0}
                  onClick={() => handleClick(n)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleClick(n)
                  }}
                  className={`group flex cursor-pointer items-start gap-4 px-5 py-4 transition-colors hover:bg-gray-50 ${
                    n.is_read ? '' : 'bg-blue-50/30'
                  }`}
                >
                  {/* Unread indicator */}
                  <div className="flex flex-col items-center pt-1">
                    {n.is_read ? (
                      <span className="size-2.5" />
                    ) : (
                      <span className="size-2.5 rounded-full bg-blue-500" />
                    )}
                  </div>

                  {/* Icon */}
                  <div
                    className={`mt-0.5 flex size-9 shrink-0 items-center justify-center rounded-full ${iconBg}`}
                  >
                    <Icon className={`size-4.5 ${iconColor}`} />
                  </div>

                  {/* Content */}
                  <div className="min-w-0 flex-1">
                    <div className="flex items-start justify-between gap-2">
                      <p
                        className={`text-sm leading-snug ${n.is_read ? 'text-gray-700' : 'font-semibold text-gray-900'}`}
                      >
                        {n.title}
                      </p>
                      <span className="shrink-0 text-xs text-gray-400">
                        {formatRelativeTime(n.created_at)}
                      </span>
                    </div>
                    <p className="mt-0.5 text-sm text-gray-500">{n.message}</p>
                    <div className="mt-1.5 flex items-center gap-3">
                      {n.action_label && (
                        <span className="text-xs font-medium text-blue-600">{n.action_label}</span>
                      )}
                      <span className="text-xs text-gray-400">{formatDate(n.created_at)}</span>
                    </div>
                  </div>

                  {/* Dismiss */}
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation()
                      dismissMutation.mutate(n.id)
                    }}
                    className="mt-1 shrink-0 rounded p-1 text-gray-400 opacity-0 transition-opacity group-hover:opacity-100 hover:bg-gray-200 hover:text-gray-600"
                    aria-label="Remover"
                  >
                    <svg
                      className="size-4"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth={2}
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              )
            })}
          </div>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between px-1">
          <p className="text-sm text-gray-500">
            {total} notificação{total === 1 ? '' : 'es'} no total
          </p>
          <div className="flex items-center gap-2">
            <button
              type="button"
              disabled={page === 0}
              onClick={() => setPage(page - 1)}
              className="rounded-lg border border-gray-300 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            >
              Anterior
            </button>
            <span className="text-sm text-gray-500">
              {page + 1} de {totalPages}
            </span>
            <button
              type="button"
              disabled={page >= totalPages - 1}
              onClick={() => setPage(page + 1)}
              className="rounded-lg border border-gray-300 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            >
              Próxima
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

export default NotificationsPage
